import React, { useEffect, useRef, useState } from "react";

import {
  FarmFootprintingCapabilities,
  getFarmFootprintingCapabilities,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingFarmResourceConsumption,
  useMutateFootprintingFarmResourceConsumption,
  useTranslation,
} from "utils";

import { AlertBanner, Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

import { FarmingTypeHelperText } from "pages/app/footprinting/components/formFlows/shared/FarmingTypeHelperText";

interface FarmFuelProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

// Typical non-field diesel use, shown only for the production types the carbon program covers
const DIESEL_BENCHMARKS = [
  { capability: "arableEnabled", copyKey: "arable" },
  { capability: "dairyEnabled", copyKey: "dairy" },
  { capability: "beefEnabled", copyKey: "beef" },
  { capability: "porkEnabled", copyKey: "pork" },
] as const satisfies ReadonlyArray<{ capability: keyof FarmFootprintingCapabilities; copyKey: string }>;

const FarmFuelForm = ({ primaryDataId, readOnly, farmId, year }: FarmFuelProps) => {
  const { t } = useTranslation("footprintingFarm");
  const formRef = useRef<FormInstance>(null);
  const carbonProgram = useGetCarbonProgramByFarmId(farmId);

  const [dieselL, setDieselL] = useState<number | null>(null);
  const [petrolL, setPetrolL] = useState<number | null>(null);
  const [naturalGasM3, setNautralGasM3] = useState<number | null>(null);
  const [heatingOilL, setHeatingOilL] = useState<number | null>(null);
  const [heatPumpKwh, setHeatPumpKwh] = useState<number | null>(null);
  const [biogasKwh, setBiogasKwh] = useState<number | null>(null);
  const [woodChipsKwh, setWoodChipsKwh] = useState<number | null>(null);

  const capabilities = getFarmFootprintingCapabilities(carbonProgram.data);
  const { dairyEnabled, arableEnabled, porkEnabled } = capabilities;
  const dieselHelp = [
    t("fuelForm.help.diesel"),
    ...DIESEL_BENCHMARKS.filter(({ capability }) => capabilities[capability]).map(({ copyKey }) => t(`fuelForm.help.dieselBenchmark.${copyKey}`)),
  ].join(" ");

  const farmResourceConsumptionQuery = useGetFootprintingFarmResourceConsumption(primaryDataId ?? "");
  const { mutate: mutateFarmResourceConsumption } = useMutateFootprintingFarmResourceConsumption(
    farmId,
    year,
    {
      id: farmResourceConsumptionQuery.data?.id,
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      dieselL,
      petrolL,
      naturalGasM3,
      heatingOilL,
      ...(porkEnabled && { heatPumpKwh, biogasKwh, woodChipsKwh }),
    },
    primaryDataId
  );

  const isLoading = queryIsLoading(farmResourceConsumptionQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFarmResourceConsumption();
      }
    },
    [isLoading, mutateFarmResourceConsumption, readOnly]
  );

  useEffect(() => {
    if (farmResourceConsumptionQuery.data) {
      setDieselL(farmResourceConsumptionQuery.data.dieselL ?? null);
      setPetrolL(farmResourceConsumptionQuery.data.petrolL ?? null);
      setNautralGasM3(farmResourceConsumptionQuery.data.naturalGasM3 ?? null);
      setHeatingOilL(farmResourceConsumptionQuery.data.heatingOilL ?? null);
      setHeatPumpKwh(farmResourceConsumptionQuery.data.heatPumpKwh ?? null);
      setBiogasKwh(farmResourceConsumptionQuery.data.biogasKwh ?? null);
      setWoodChipsKwh(farmResourceConsumptionQuery.data.woodChipsKwh ?? null);

      formRef.current?.setFieldsValue({
        dieselL: farmResourceConsumptionQuery.data.dieselL ?? null,
        petrolL: farmResourceConsumptionQuery.data.petrolL ?? null,
        naturalGasM3: farmResourceConsumptionQuery.data.naturalGasM3 ?? null,
        heatingOilL: farmResourceConsumptionQuery.data.heatingOilL ?? null,
        heatPumpKwh: farmResourceConsumptionQuery.data.heatPumpKwh ?? null,
        biogasKwh: farmResourceConsumptionQuery.data.biogasKwh ?? null,
        woodChipsKwh: farmResourceConsumptionQuery.data.woodChipsKwh ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["dieselL", "petrolL", "naturalGasM3", "heatingOilL"] as const).some(
        (field) => farmResourceConsumptionQuery.data[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [farmResourceConsumptionQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef}>
      <Flex vertical gap={16}>
        <Card border dataTestId="footprinting-farm-fuel-form" className="padding-200">
          <Flex vertical gap={16}>
            {(dairyEnabled || arableEnabled) && (
              <AlertBanner
                type="info"
                description={
                  <Flex vertical gap={16} className="footprinting-fuel-guidance-banner">
                    <FarmingTypeHelperText
                      layout="section"
                      visible={dairyEnabled}
                      heading={t("fuelForm.guidanceBanner.milkProductionHeading")}
                      description={t("fuelForm.guidanceBanner.milkProductionBody")}
                    />
                    <FarmingTypeHelperText
                      layout="section"
                      visible={arableEnabled}
                      heading={t("fuelForm.guidanceBanner.arableFarmingHeading")}
                      description={t("fuelForm.guidanceBanner.arableFarmingBody")}
                    />
                  </Flex>
                }
              />
            )}
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-diesel-input"
              disabled={readOnly}
              name="dieselL"
              label={t("fuelForm.question.dieselQuestionLabel")}
              tooltip={t("fuelForm.tooltips.dieselQuestionLabel")}
              suffix="L"
              value={dieselL}
              initialValue={dieselL}
              onChange={(v) => setDieselL(v)}
              help={
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {dieselHelp}
                </Typography>
              }
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-petrol-input"
              name="petrolL"
              disabled={readOnly}
              label={t("fuelForm.question.petrolQuestionLabel")}
              tooltip={t("fuelForm.tooltips.petrolQuestionLabel")}
              suffix="L"
              value={petrolL}
              initialValue={petrolL}
              onChange={(v) => setPetrolL(v)}
              help={
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("fuelForm.help.petrol")}
                </Typography>
              }
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-gas-input"
              disabled={readOnly}
              name="naturalGasM3"
              label={t("fuelForm.question.naturalGasQuestionLabel")}
              tooltip={t("fuelForm.tooltips.naturalGasQuestionLabel")}
              suffix="m³"
              value={naturalGasM3}
              initialValue={naturalGasM3}
              onChange={(v) => setNautralGasM3(v)}
              help={
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("fuelForm.help.naturalGas")}
                </Typography>
              }
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
            <InputNumber
              className="floating-label"
              dataTestId="footprinting-farm-oil-input"
              name="heatingOilL"
              disabled={readOnly}
              label={t("fuelForm.question.heatingOilQuestionLabel")}
              tooltip={t("fuelForm.tooltips.heatingOilQuestionLabel")}
              suffix="L"
              value={heatingOilL}
              initialValue={heatingOilL}
              onChange={(v) => setHeatingOilL(v)}
              help={
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("fuelForm.help.heatingOil")}
                </Typography>
              }
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>

        {porkEnabled && (
          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("fuelForm.porkCard.title")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("fuelForm.porkCard.subTitle")}
                </Typography>
              </Flex>
              <InputNumber
                className="floating-label"
                dataTestId="footprinting-farm-heat-pump-input"
                name="heatPumpKwh"
                disabled={readOnly}
                label={t("fuelForm.question.heatPumpQuestionLabel")}
                suffix="kWh"
                value={heatPumpKwh}
                initialValue={heatPumpKwh}
                onChange={(v) => setHeatPumpKwh(v)}
                help={
                  <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                    {t("fuelForm.help.heatPump")}
                  </Typography>
                }
                rules={[
                  { required: true, message: " " },
                  { type: "number", message: " " },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
              <InputNumber
                className="floating-label"
                dataTestId="footprinting-farm-biogas-input"
                name="biogasKwh"
                disabled={readOnly}
                label={t("fuelForm.question.biogasQuestionLabel")}
                suffix="kWh"
                value={biogasKwh}
                initialValue={biogasKwh}
                onChange={(v) => setBiogasKwh(v)}
                help={
                  <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                    {t("fuelForm.help.biogas")}
                  </Typography>
                }
                rules={[
                  { required: true, message: " " },
                  { type: "number", message: " " },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
              <InputNumber
                className="floating-label"
                dataTestId="footprinting-farm-wood-chips-input"
                name="woodChipsKwh"
                disabled={readOnly}
                label={t("fuelForm.question.woodChipsQuestionLabel")}
                suffix="kWh"
                value={woodChipsKwh}
                initialValue={woodChipsKwh}
                onChange={(v) => setWoodChipsKwh(v)}
                help={
                  <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                    {t("fuelForm.help.woodChips")}
                  </Typography>
                }
                rules={[
                  { required: true, message: " " },
                  { type: "number", message: " " },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("fuelForm.error.fuelQuestionError")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>
        )}
      </Flex>
    </Form>
  );
};

FarmFuelForm.Key = "farm.fuel";
export { FarmFuelForm };
